<template>
  <v-container class="my-4">
    <v-card class="elevation-4">
      <v-card-title class="page-head">
        <div class="ph-left">
          <div class="ph-icon"><v-icon color="#6a1b9a">mdi-account-heart</v-icon></div>
          <div>
            <div class="ph-title">Care Giver Assessment Report</div>
            <div class="ph-sub">Assessments filtered by care giver</div>
          </div>
        </div>
        <v-spacer />
        <v-btn class="ph-btn" @click="printReport">
          <v-icon left small>mdi-printer</v-icon>
          Print
        </v-btn>
      </v-card-title>

      <v-card-text>
        <!-- Filters -->
        <v-row dense>
          <v-col cols="12" md="4">
            <div class="mb-1 caption font-weight-medium text-uppercase grey--text text--darken-1">
              Care Giver
            </div>
            <Select2
              v-model="filters.mother_id"
              :options="motherOptions"
              id="mother-report-mother"
              :settings="{ width: '100%' }"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="filters.from_date"
              type="date"
              label="From date"
              dense
              outlined
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="filters.to_date"
              type="date"
              label="To date"
              dense
              outlined
            />
          </v-col>
        </v-row>

        <v-row class="mt-2" dense>
          <v-col cols="12" class="d-flex justify-end">
            <v-btn small text class="mr-2" @click="resetFilters">Reset</v-btn>
            <v-btn small color="primary" dark @click="runReport">Apply</v-btn>
          </v-col>
        </v-row>

        <!-- Report table -->
        <div id="mother-report-print" class="mt-4">
          <div class="print-header">
            <img :src="logoSrc" alt="Logo" class="print-logo" />
            <div class="print-date">{{ currentDate }}</div>
          </div>
          <!-- Mini care giver profile summary -->
          <v-row v-if="rows.length" dense class="mb-2">
            <v-col cols="12" md="8">
              <div class="subtitle-1 font-weight-medium">Care Giver Assessment Report</div>
              <div class="caption grey--text">Filtered by care giver and date range</div>
              <div class="body-2 mt-1">
                <strong>Care Giver:</strong> {{ rows[0].mothername }}
                <span v-if="rows[0].mother_sex"> • <strong>Sex:</strong> {{ rows[0].mother_sex }}</span>
              </div>
              <div class="body-2" v-if="rows[0].contact">
                <strong>Contact:</strong> {{ rows[0].contact }}
              </div>
              <div class="body-2 mt-1">
                <strong>Children under care (in this period):</strong> {{ distinctChildren }}
              </div>
            </v-col>
            <v-col cols="12" md="4" class="text-right d-flex flex-column justify-end">
              <div class="caption">Total visits: {{ rows.length }}</div>
            </v-col>
          </v-row>
          <v-row v-else dense>
            <v-col cols="12">
              <div class="subtitle-1 font-weight-medium">Care Giver Assessment Report</div>
              <div class="caption grey--text">No records for the selected filters.</div>
            </v-col>
          </v-row>

          <v-text-field
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            label="Search"
            dense
            outlined
            hide-details
            class="mt-2"
          />

          <v-data-table
            class="mt-2 elevation-1"
            :headers="headers"
            :items="rows"
            :search="search"
            dense
          >
            <template v-slot:item.index="{ index }">
              {{ index + 1 }}
            </template>
          </v-data-table>
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import axios from 'axios';
import Select2 from 'v-select2-component';
import LogoImg from '@/assets/realmow.png';

const API_URL = process.env.VUE_APP_API_URL;

export default {
  name: 'MotherReport',
  components: { Select2 },
  data() {
    return {
      search: '',
      motherOptions: [],
      rows: [],
      filters: {
        mother_id: null,
        from_date: '',
        to_date: '',
      },
      distinctChildren: 0,
      logoSrc: LogoImg,
      currentDate: new Date().toLocaleDateString(),
      headers: [
        { text: 'No.', value: 'index' },
        { text: 'Visit Date', value: 'visit_date' },
        { text: 'Child', value: 'childname' },
        { text: 'Care Giver', value: 'mothername' },
        { text: 'Is Sick', value: 'is_sick' },
        { text: 'Nutrition Status', value: 'nutrition_status' },
        { text: 'Mental Status', value: 'mental_status' },
      ],
    };
  },
  mounted() {
    this.fetchMothers();
  },
  methods: {
    fetchMothers() {
      // Reuse existing mothers endpoint from Assignchild (opcion 46)
      axios
        .post(API_URL, { opcion: 46, usr_id: (JSON.parse(localStorage.getItem('user') || '{}')).id || '' })
        .then((response) => {
          this.motherOptions = response.data || [];
        })
        .catch(() => {
          this.motherOptions = [];
        });
    },
    resetFilters() {
      this.filters.mother_id = null;
      this.filters.from_date = '';
      this.filters.to_date = '';
    },
    runReport() {
      axios
        .post(API_URL, { opcion: 20, op: 0, assessedby: (JSON.parse(localStorage.getItem('user') || '{}')).id || '' })
        .then((res) => {
          const all = Array.isArray(res.data)
            ? res.data
            : Array.isArray(res.data && res.data.data)
            ? res.data.data
            : [];

          this.rows = all.filter((r) => {
            if (this.filters.mother_id && String(r.mother_id) !== String(this.filters.mother_id)) return false;
            if (this.filters.from_date && r.visit_date < this.filters.from_date) return false;
            if (this.filters.to_date && r.visit_date > this.filters.to_date) return false;
            return true;
          });
        })
        .catch(() => {
          this.rows = [];
        });
    },
    printReport() {
      const prtHtml = document.getElementById('mother-report-print')?.innerHTML;
      if (!prtHtml) {
        window.print();
        return;
      }

      let stylesHtml = '';
      const styleNodes = document.querySelectorAll('link[rel="stylesheet"], style');
      styleNodes.forEach((node) => {
        stylesHtml += node.outerHTML;
      });

      const WinPrint = window.open('', '', 'left=0,top=0,width=800,height=900,toolbar=0,scrollbars=0,status=0');
      WinPrint.document.write(`<!DOCTYPE html>
        <html>
          <head>
            ${stylesHtml}
          </head>
          <body>
            ${prtHtml}
          </body>
        </html>`);
      WinPrint.document.close();

      WinPrint.onload = () => {
        WinPrint.focus();
        WinPrint.print();
        WinPrint.close();
      };
    },
  },
};
</script>

<style scoped>
.print-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding: 10px;
}

.print-logo {
  height: 80px;
}

.print-date {
  font-size: 12px;
  color: #444;
}
</style>
